/**
 * Modal "Pickear" — pickeo agrupado por lote.
 *
 * Orden en pantalla:
 *   1. Campo de lectura.
 *   2. Acumulador por lote: una línea por lote con lo pickeado sobre lo
 *      esperado (y el artículo, o los artículos, de ese lote).
 *   3. Última lectura: qué trajo el escáner y qué pasó con eso.
 *   4. Lista de unidades pickeadas, una fila por serie, la más reciente arriba.
 *
 * Solo se acepta lo que pertenece al detalle original; el resto se informa
 * como no encontrado. El estado lo aporta el padre vía `usePickeoLotes`, así
 * sobrevive al cierre del modal. La captura (separador GS, cola, vaciado
 * sincrónico del campo) está en `CampoLecturaGs1`.
 */

import { useMemo, useRef, useState } from 'react';
import { CheckCircle2, Loader2, Trash2, Volume2 } from 'lucide-react';
import { ConfirmarQuitarPickeoDialog } from './ConfirmarQuitarPickeoDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/notify';
import { probarSonido } from '@/lib/audio';
import { vencimientoGs1AMesAnio } from '@/domain/gs1';
import { CampoLecturaGs1 } from './CampoLecturaGs1';
import { lectorEnRafaga } from './rafaga-lector';
import type { UsePickeoLotesResult } from './usePickeoLotes';
import type { ResultadoPickeo, VistaLote } from './pickeo-lotes';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pickeo: UsePickeoLotesResult;
  /** Número AFIP del remito, para el título. */
  numeroAfip: string | null;
  referencia: string | null;
  /** False con la traza en curso o terminada: no se ofrece quitar series. */
  permiteQuitar?: boolean;
}

/** `YYYY-MM-DD …` del ERP → `MM/YYYY`. */
function vencimientoErpAMesAnio(value: string | null): string | null {
  const m = /^(\d{4})-(\d{2})/.exec(value ?? '');
  const yyyy = m?.[1];
  const mm = m?.[2];
  return yyyy && mm ? `${mm}/${yyyy}` : null;
}

export function PickeoModal({
  open,
  onOpenChange,
  pickeo,
  numeroAfip,
  referencia,
  permiteQuitar = true,
}: Props) {
  const { state, resumen, vista, grupos, lectura, quitar } = pickeo;
  const inputRef = useRef<HTMLInputElement>(null);
  const ultimo = state.ultimoResultado;

  const grupoPorKey = useMemo(() => new Map(grupos.map((g) => [g.key, g])), [grupos]);
  // Una serie suelta solo se puede ubicar si el ERP declara las series del
  // remito (formato por serie). En un remito por lote, solo el DataMatrix trae
  // artículo, lote y serie juntos.
  const aceptaSerieSuelta = useMemo(() => grupos.some((g) => g.series.length > 0), [grupos]);

  // "Probar sonido": reproduce los dos beeps y avisa si el navegador no tiene
  // dónde reproducir. El clic es además la interacción que el navegador exige
  // para habilitar el audio. El botón no toma el foco (ver onMouseDown) para
  // que el campo de lectura siga recibiendo al lector.
  const probarAudio = async () => {
    const ok = await probarSonido();
    if (!ok) {
      toast.warning(
        'El navegador no pudo reproducir sonido. Revisá parlantes, volumen y que la pestaña no esté silenciada.',
      );
    }
  };
  // Más reciente arriba: el operador ve la última unidad sin desplazarse.
  const unidades = useMemo(() => [...state.pickeos].reverse(), [state.pickeos]);
  // Serie a quitar, esperando confirmación. Al cerrar la confirmación, el foco
  // vuelve al campo de lectura para seguir escaneando.
  const [aQuitar, setAQuitar] = useState<string | null>(null);
  const unidadAQuitar = aQuitar ? state.pickeos.find((p) => p.serie === aQuitar) : undefined;
  const grupoAQuitar = unidadAQuitar ? grupoPorKey.get(unidadAQuitar.grupoKey) : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
        // Radix escucha el Escape en captura sobre el documento, antes que el
        // campo. No hay que cerrar el modal si el Escape viene del lector (la
        // Syble lo manda en lugar del GS) ni si el operador tiene texto en el
        // campo: en ese caso el campo lo vacía y el modal sigue abierto.
        onEscapeKeyDown={(e) => {
          if (lectorEnRafaga() || (inputRef.current?.value ?? '') !== '') e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 text-everex-azul">
            Pickeo
            {numeroAfip && <span className="font-mono">{numeroAfip}</span>}
            <span
              className={cn(
                'badge ml-auto font-normal',
                resumen.completo ? 'badge-green' : 'badge-blue',
              )}
            >
              Pickeadas {resumen.pickeadas} de {resumen.esperadas}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Escaneá cada unidad. La lista se arma con lo que vas pickeando.
            {referencia && <span className="font-mono text-gray-500"> · {referencia}</span>}
          </DialogDescription>
        </DialogHeader>

        <CampoLecturaGs1
          inputRef={inputRef}
          onLectura={lectura}
          resetKey={open}
          placeholder={
            aceptaSerieSuelta
              ? 'Escaneá el DataMatrix o ingresá el N° de serie y presioná Enter'
              : 'Escaneá el DataMatrix de la unidad'
          }
        />

        <AcumuladorLotes vista={vista} />

        <UltimaLectura resultado={ultimo} />

        {/* Unidades pickeadas, una por serie */}
        <div
          className="max-h-[36vh] overflow-y-auto rounded-md border border-gray-200"
          // Un clic sobre las filas no debe robarle el foco al campo de lectura.
          onMouseDown={(e) => {
            if ((e.target as HTMLElement).closest('tbody')) e.preventDefault();
          }}
        >
          {unidades.length === 0 ? (
            <p className="px-4 py-8 text-center text-xs text-gray-500">
              Todavía no se pickeó ninguna unidad. Escaneá la primera.
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 text-[10px] uppercase text-gray-600">
                <tr>
                  <th className="px-3 py-1.5 text-left">Artículo</th>
                  <th className="px-3 py-1.5 text-left">GTIN</th>
                  <th className="px-3 py-1.5 text-left">Lote</th>
                  <th className="px-3 py-1.5 text-left">Vencimiento</th>
                  <th className="px-3 py-1.5 text-left">N° de serie</th>
                  <th className="px-3 py-1.5" aria-label="Acciones" />
                </tr>
              </thead>
              <tbody>
                {unidades.map((u, i) => {
                  const g = grupoPorKey.get(u.grupoKey);
                  const esUltima = i === 0 && ultimo?.tipo === 'pickeada';
                  return (
                    <tr
                      key={u.serie}
                      className={cn(
                        'border-t border-gray-100',
                        esUltima && 'bg-green-50 transition-colors',
                      )}
                    >
                      <td className="px-3 py-1.5">{g?.nombre ?? g?.sku ?? '—'}</td>
                      <td className="px-3 py-1.5 font-mono text-gray-600">
                        {g?.gtin ?? u.lectura.gtin ?? '—'}
                      </td>
                      <td className="px-3 py-1.5 font-mono">{g?.lote ?? u.lectura.lote ?? '—'}</td>
                      <td className="px-3 py-1.5">
                        {vencimientoErpAMesAnio(g?.vencimiento ?? null) ??
                          vencimientoGs1AMesAnio(u.lectura.vencimiento) ??
                          '—'}
                      </td>
                      <td className="px-3 py-1.5 font-mono font-semibold">{u.serie}</td>
                      <td className="px-3 py-1.5 text-right">
                        {!permiteQuitar ? null : u.stockId ? (
                          <button
                            type="button"
                            className="rounded text-red-600 hover:text-red-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                            title={`Quitar la serie ${u.serie} del pickeo`}
                            aria-label={`Quitar la serie ${u.serie}`}
                            onClick={() => setAQuitar(u.serie)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        ) : (
                          <Loader2
                            className="ml-auto h-4 w-4 animate-spin text-gray-400"
                            aria-label="Guardando"
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {aQuitar && (
          <ConfirmarQuitarPickeoDialog
            open
            onOpenChange={(o) => {
              if (!o) {
                setAQuitar(null);
                inputRef.current?.focus();
              }
            }}
            onConfirmar={() => quitar(aQuitar)}
            articulo={grupoAQuitar?.nombre ?? grupoAQuitar?.sku ?? null}
            lote={grupoAQuitar?.lote ?? unidadAQuitar?.lectura.lote ?? null}
            serie={aQuitar}
          />
        )}

        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-gray-600"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              void probarAudio();
            }}
          >
            <Volume2 />
            Probar sonido
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Acumulador: una línea por lote con lo pickeado sobre lo esperado. */
function AcumuladorLotes({ vista }: { vista: VistaLote[] }) {
  if (vista.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-gray-200 px-3 py-2 text-xs text-gray-500">
        Acumulado por lote: todavía sin pickeos.
      </div>
    );
  }
  return (
    <div className="divide-y divide-everex-azul-bg-2 rounded-md border border-everex-azul-bg-2 bg-everex-azul-bg/60 text-xs">
      {vista.map((lote) => {
        const completo = lote.pickeadas >= lote.esperadas;
        const unico = lote.articulos.length === 1 ? lote.articulos[0] : null;
        return (
          <div key={lote.lote} className="px-3 py-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {unico && (
                <span className="font-semibold text-gray-800">
                  {unico.nombre ?? unico.sku ?? '—'}
                </span>
              )}
              <span className="font-semibold text-everex-azul">
                Lote <span className="font-mono">{lote.lote}</span>
              </span>
              {lote.vencimiento && <span className="text-gray-600">Vto {lote.vencimiento}</span>}
              <span className="ml-auto font-mono">
                <b className={cn(completo ? 'text-green-700' : 'text-everex-azul')}>
                  {lote.pickeadas}
                </b>{' '}
                <span className="text-gray-600">de {lote.esperadas}</span>
              </span>
              {completo && (
                <CheckCircle2 className="h-4 w-4 text-green-600" aria-label="Lote completo" />
              )}
            </div>
            {!unico && (
              <ul className="mt-1 space-y-0.5 pl-4">
                {lote.articulos.map((a) => (
                  <li key={a.grupoKey} className="flex items-center gap-2 text-gray-800">
                    <span>{a.nombre ?? a.sku ?? '—'}</span>
                    <span className="ml-auto font-mono">
                      {a.pickeadas} <span className="text-gray-600">de {a.esperadas}</span>
                    </span>
                    {a.pickeadas >= a.esperadas && (
                      <CheckCircle2
                        className="h-3.5 w-3.5 text-green-600"
                        aria-label="Artículo completo"
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Panel: qué trajo el escáner y qué pasó con eso. */
function UltimaLectura({ resultado }: { resultado: ResultadoPickeo | null }) {
  const l = resultado?.lectura ?? null;
  const estado = (() => {
    if (!resultado || resultado.tipo === 'vacia') return null;
    switch (resultado.tipo) {
      case 'pickeada':
        return { texto: 'Pickeada', clase: 'border-green-200 bg-green-50 text-green-900' };
      case 'duplicada':
        return {
          texto: 'Esta unidad ya estaba pickeada',
          clase: 'border-amber-200 bg-amber-50 text-amber-900',
        };
      case 'excedente':
        return { texto: 'Lote completo', clase: 'border-amber-300 bg-amber-50 text-amber-900' };
      case 'articulo_ajeno':
        return {
          texto: 'Artículo no encontrado en el pedido',
          clase: 'border-red-200 bg-red-50 text-red-900',
        };
      case 'lote_ajeno':
        return {
          texto: 'Lote no encontrado en el pedido',
          clase: 'border-red-200 bg-red-50 text-red-900',
        };
      case 'solo_serie':
        return {
          texto: 'Solo número de serie',
          clase: 'border-amber-300 bg-amber-50 text-amber-900',
        };
      case 'solo_gtin':
        return {
          texto: 'Código de barras del producto',
          clase: 'border-amber-300 bg-amber-50 text-amber-900',
        };
      case 'sin_separador':
        return {
          texto: 'Lote y serie sin delimitar',
          clase: 'border-red-200 bg-red-50 text-red-900',
        };
      case 'rechazada':
        return {
          texto: 'No se pudo pickear',
          clase: 'border-red-200 bg-red-50 text-red-900',
        };
      case 'sin_serie':
      case 'ilegible':
        return { texto: 'Lectura no válida', clase: 'border-red-200 bg-red-50 text-red-900' };
    }
  })();

  return (
    <div className={cn('rounded-md border p-3', estado?.clase ?? 'border-gray-200 bg-gray-50')}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider opacity-70">
          Última lectura
        </span>
        {estado ? (
          <span className="text-sm font-semibold">{estado.texto}</span>
        ) : (
          <span className="text-xs text-gray-500">Esperando escaneo…</span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm md:grid-cols-4">
        <Dato etiqueta="GTIN" valor={l?.gtin ?? null} />
        <Dato etiqueta="Lote" valor={l?.lote ?? null} />
        <Dato etiqueta="Vencimiento" valor={vencimientoGs1AMesAnio(l?.vencimiento)} />
        <Dato etiqueta="N° de serie" valor={l?.serie ?? null} />
      </div>
      {/* El detalle solo cuando agrega algo al título: en artículo/lote ajeno
          el mensaje es el mismo título y se vería dos veces. */}
      {resultado &&
        resultado.tipo !== 'vacia' &&
        resultado.tipo !== 'pickeada' &&
        resultado.tipo !== 'articulo_ajeno' &&
        resultado.tipo !== 'lote_ajeno' && (
          <p className="mt-2 text-xs opacity-90">{resultado.mensaje}</p>
        )}
    </div>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string | null }) {
  return (
    <div>
      <div className="font-sans text-[10px] uppercase tracking-wider opacity-70">{etiqueta}</div>
      <div className={cn('truncate', !valor && 'opacity-40')} title={valor ?? undefined}>
        {valor ?? '—'}
      </div>
    </div>
  );
}
